"use client";
import {
  EmailRegex,
  Login,
  NonEmptyStringRegex,
  Redirection
} from "@rinastyling/common";
import { useSetAtom } from "jotai";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useInput from "../hooks/useInput";
import { usePopover } from "../hooks/usePopover";
import useRequiredFields from "../hooks/useRequiredFields";
import { AuthService } from "../services/AuthService";
import { userAtom } from "../store/userAtom";
import { FlexCoulmn } from "../styles/globalsStyles";
import { handleFetchError } from "../utils/clientErrorHandeling";
import { Routes } from "../utils/clientRoutes";
import ForgotPasswordForm from "./ForgotPasswordForm";
import { OutsideLink } from "./OutsideLink";
import TitleWithBorder from "./TitleWithBorder";
import { Button } from "./core/Button";
import { Form } from "./core/Form";
import { revalidateUserData } from "../actions/profile.actions";

interface LoginFormProps {
  cancelEndRouting?: boolean;
}

const LoginForm = ({ cancelEndRouting }: LoginFormProps) => {
  const emailInput = useInput({
    initalValue: "",
    direction: "ltr",
    regex: EmailRegex,
    placeholder: "myEmail@gmail.com",
    label: "אימייל"
  });
  const passwordInput = useInput({
    initalValue: "",
    direction: "ltr",
    regex: NonEmptyStringRegex,
    label: "סיסמא",
    type: "password"
  });
  const passworlesInput = useInput<boolean>({
    initalValue: false,
    label: "להשתמש בסיסמא חד פעמית?",
    type: "toggle"
  });
  const otpInput = useInput({
    initalValue: "",
    label: "המספרים שהגיעו במייל",
    type: "text"
  });
  const [passwordLessLoginToken, setPasswordLessLoginToken] = useState("");
  const regularLoginDisabled = useRequiredFields([passwordInput, emailInput]);
  const searchParams = useSearchParams();

  const step = useMemo(() => {
    if (passworlesInput.value && !passwordLessLoginToken) return "firstOtp";
    if (passworlesInput.value && passwordLessLoginToken) return "secondOtp";
    if (!passworlesInput.value) return "normalLogin";
    return "normalLogin";
  }, [passwordLessLoginToken, passworlesInput.value]);

  const disabled = useMemo(() => {
    if (passworlesInput.value) {
      return !emailInput.value || emailInput.isError;
    } else return regularLoginDisabled;
  }, [
    emailInput.isError,
    emailInput.value,
    passworlesInput.value,
    regularLoginDisabled
  ]);

  const getRedirection = useCallback(() => {
    const redirection = searchParams.get("redirection");
    if (!redirection) return null;
    try {
      return JSON.parse(redirection) as Redirection;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [searchParams]);

  const setUser = useSetAtom(userAtom);
  const router = useRouter();
  const handlePasswordlessRequest = useCallback(async () => {
    try {
      const res = await new AuthService().passwordlessLoginRequest({
        email: emailInput.value
      });
      if (res.token) {
        setPasswordLessLoginToken(res.token);
      }
    } catch (error) {
      handleFetchError(error);
    }
  }, [emailInput.value]);

  const onSubmit = useCallback(async () => {
    try {
      let res: Login["response"] = null!;

      if (step === "normalLogin") {
        res = await new AuthService().login({
          email: emailInput.value,
          password: passwordInput.value
        });
      }
      if (step === "secondOtp") {
        res = await new AuthService().passwordlessLoginSubmit({
          email: emailInput.value,
          otp: otpInput.value,
          token: passwordLessLoginToken
        });
      }
      if (!res.user || !res.accessToken)
        throw new Error((res as { msg: string }).msg);
      toast(res.msg);
      revalidateUserData();
      setUser({ ...res.user, accessToken: res.accessToken });

      if (cancelEndRouting) return;
      const redirection = getRedirection();
      if (redirection) {
        router.push(redirection.url);
        return;
      }

      if (res.user.isAdmin) {
        // router.push(Routes.adminHomePage);
        router.push(Routes.home);
      }
      if (!res.user.isAdmin) {
        // router.push(Routes.userCourses);
        router.push(Routes.home);
      }
    } catch (error) {
      console.log(error);
      handleFetchError(error);
    }
  }, [
    cancelEndRouting,
    emailInput.value,
    getRedirection,
    otpInput.value,
    passwordInput.value,
    passwordLessLoginToken,
    router,
    setUser,
    step
  ]);
  const { showPopover } = usePopover();
  const handleForgotPasswordClick = useCallback(() => {
    showPopover(<ForgotPasswordForm />);
  }, [showPopover]);

  return (
    <FlexCoulmn $alignItems="center" $gap={"1rem"}>
      <TitleWithBorder text="התחברות" width={200} />
      <Form>
        {emailInput.jsx}
        <FlexCoulmn $alignItems="center">{passworlesInput.jsx}</FlexCoulmn>

        {step === "normalLogin" && passwordInput.jsx}
        {step === "secondOtp" && otpInput.jsx}

        <Button
          onClick={() =>
            step === "firstOtp" ? handlePasswordlessRequest() : onSubmit()
          }
          disabled={disabled}
        >
          {step === "normalLogin" || step === "secondOtp"
            ? "התחבר"
            : "שליחת מייל עם ססמא חד פעמית"}
        </Button>

        <OutsideLink onClick={handleForgotPasswordClick}>
          שכחת סיסמא?
        </OutsideLink>
      </Form>
    </FlexCoulmn>
  );
};

export default LoginForm;
